import { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { MapPin, Phone, Mail, Send, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/quote`, formData);
      setIsSubmitted(true);
      toast.success("Tack för din förfrågan! Vi återkommer inom 24 timmar.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Något gick fel. Försök igen eller ring oss direkt.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      label: "Adress",
      value: "Wedavägen 14B, 152 42 Södertälje",
    },
    {
      icon: Phone,
      label: "Telefon",
      value: "072-293 09 14",
      href: "tel:0722930914",
    },
    {
      icon: Mail,
      label: "E-post",
      value: "info@optimalel.se",
      href: "mailto:info@optimalel.se",
    },
  ];

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      ref={ref}
      className="py-24 md:py-32 bg-[#050505]"
    >
      <div className="container mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left Column - Contact Info */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4"
            >
              Kontakt
            </motion.p>

            <motion.h2
              data-testid="contact-title"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-3xl md:text-4xl lg:text-5xl font-manrope font-light text-white mb-6 tracking-tight"
            >
              Låt oss skapa
              <br />
              <span className="text-zinc-500">något extraordinärt.</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-zinc-400 text-base md:text-lg leading-relaxed mb-10"
            >
              Berätta om ditt projekt så återkommer vi med en kostnadsfri offert 
              inom 24 timmar. Inga förpliktelser.
            </motion.p>

            {/* Contact Details */}
            <div className="space-y-6">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={index}
                  data-testid={`contact-info-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="p-3 bg-[#0A0A0A] border border-white/5">
                    <item.icon className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">
                      {item.label}
                    </div>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="text-white hover:text-[#D4AF37] transition-colors"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <div className="text-white">{item.value}</div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-[#0A0A0A] border border-white/5 p-8 md:p-10"
          >
            {isSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="h-full flex flex-col items-center justify-center text-center py-12"
              >
                <CheckCircle className="w-16 h-16 text-[#D4AF37] mb-6" />
                <h3 className="text-2xl font-manrope font-light text-white mb-4">
                  Tack för din förfrågan!
                </h3>
                <p className="text-zinc-400 mb-8">
                  Vi återkommer inom 24 timmar med en offert.
                </p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="text-[#D4AF37] hover:text-white transition-colors"
                >
                  Skicka en ny förfrågan
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="quote-form">
                <h3 className="text-xl font-manrope font-medium text-white mb-8">
                  Begär kostnadsfri offert
                </h3>

                <div className="space-y-6">
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Ditt namn"
                      required
                      data-testid="quote-form-name"
                      className="input-minimal"
                    />
                  </div>

                  <div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="E-postadress"
                      required
                      data-testid="quote-form-email"
                      className="input-minimal"
                    />
                  </div>

                  <div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Telefonnummer"
                      required
                      data-testid="quote-form-phone"
                      className="input-minimal"
                    />
                  </div>

                  <div>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Beskriv ditt projekt..."
                      required
                      rows={4}
                      data-testid="quote-form-message"
                      className="input-minimal resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="quote-form-submit"
                    className="w-full bg-[#D4AF37] text-black py-4 font-medium tracking-wide hover:bg-[#C5A028] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Skickar...
                      </>
                    ) : (
                      <>
                        Skicka Förfrågan
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>

                <p className="text-zinc-600 text-xs mt-6 text-center">
                  Genom att skicka godkänner du att vi kontaktar dig angående din förfrågan.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
